import { useCallback, useMemo, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { DOCUMENT_SUBMISSION_METHODS } from '../../onboarding/constants';
import { DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES } from '../components/documentUploadDialog';
import { useDocumentUploadSectionContext } from '../components/documentUploadSection';

import type { ChangeEvent } from 'react';
import type { UseDocumentUploadProps, UseDocumentUploadResult } from './types';

export default function useDocumentUpload({
  onFile,
}: UseDocumentUploadProps): UseDocumentUploadResult {
  const { submissionMethod, isDesktopMobileCaptureHandoff } = useDocumentUploadSectionContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [initialFile, setInitialFile] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setInitialFile(null);
  }, []);

  const handleDialogSelect = useCallback(
    async (file: File, title: string) => {
      setIsDialogOpen(false);
      setInitialFile(null);
      await onFile(file, title);
    },
    [onFile],
  );

  const handleCameraChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (
      !file ||
      !Object.prototype.hasOwnProperty.call(DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES, file.type)
    ) {
      return;
    }
    // Captured files still go through the dialog so title defaults / min-length match Upload.
    setInitialFile(file);
    setIsDialogOpen(true);
  }, []);

  const open = useCallback(() => {
    if (isDesktopMobileCaptureHandoff) {
      return;
    }

    if (submissionMethod === DOCUMENT_SUBMISSION_METHODS.CAPTURED && isMobile) {
      cameraInputRef.current?.click();
      return;
    }
    setInitialFile(null);
    setIsDialogOpen(true);
  }, [isDesktopMobileCaptureHandoff, submissionMethod, isMobile]);

  return useMemo(
    () => ({
      open,
      isDialogOpen,
      closeDialog,
      handleDialogSelect,
      initialFile,
      cameraInputRef,
      handleCameraChange,
    }),
    [open, isDialogOpen, closeDialog, handleDialogSelect, initialFile, handleCameraChange],
  );
}
